import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUserGraduate } from 'react-icons/fa';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { useAuthStore } from '../../../auth/state';
import UserDashboardUX from '../../../components/ux_extra/main_pages/usr_dashboard_ux';
import './tutor_dashboard.css';
import { doc, updateDoc } from 'firebase/firestore';  // # Import for updating theme
import { db } from '../../../auth/firebase';  // # Import database
import cacheManager from '../../../auth/cache_manager';  // # Import cache manager

// # Import mentor icons
import calendarIcon from '../../../aneo_media/mentor/calendar.png';
import meetingIcon from '../../../aneo_media/mentor/meeting.png';
import menteesIcon from '../../../aneo_media/mentor/mentees.png';
import workspaceIcon from '../../../aneo_media/mentor/workspace.png';

// # Helper function to get display name - moved outside component
const getDisplayName = (userProfile, user) => {
  let name = '';
  let username = '';

  // Get name
  if (userProfile?.lastName) {
    name = `${userProfile.lastName}`;
  } else if (userProfile?.firstName) {
    name = `${userProfile.firstName}`;
  } else {
    name = `${user?.email?.split('@')[0] || 'Tutor'}`;
  }

  // Get username
  if (userProfile?.username) {
    username = `@${userProfile.username}`;
  }

  return { name, username };
};

// # Helper function to get profile photo URL - moved outside component
const getProfilePhotoUrl = (userProfile) => {
  try {
    if (!userProfile?.photoURL) return '/icons/pfp.png';
    if (userProfile.photoURL === '') return '/icons/pfp.png';
    
    // Check if it's a full URL (from Storage)
    if (userProfile.photoURL.startsWith('http')) {
      return userProfile.photoURL;
    }
    
    // If it's just a filename, assume it's the default
    return '/icons/pfp.png';
  } catch (error) {
    console.error('Error getting profile photo:', error);
    return '/icons/pfp.png';
  }
};

// # Helper function to check subscription access
const checkSubscriptionAccess = (subscriptions, type) => {
  if (!subscriptions) return false;
  
  // # Helper function to check if subscription is active
  const isActive = (subscription) => subscription?.status === 'active';
  
  // # Map subscription types to access rules
  const accessMap = {
    'step1_tutor': () => isActive(subscriptions.step1_tutor),
    'step2_tutor': () => isActive(subscriptions.step2_tutor)
  };

  return accessMap[type] ? accessMap[type]() : false;
};

// # Tutor Dashboard component
const TutorDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile, userDashboard } = useAuthStore();
  const [isDarkMode, setIsDarkMode] = useState(false);  // # Add dark mode state

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Memoize display name to prevent unnecessary recalculations
  const displayName = React.useMemo(() => 
    getDisplayName(userProfile, user), [userProfile, user]);

  // # Handle dashboard item click
  const handleItemClick = (item) => {
    if (item.locked) {
      // # Show subscription required message
      console.log('Subscription required for this feature');
      return;
    }
    
    // # Navigate to the selected item's path
    navigate(item.path);
  };

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Navigation options for mentor section with paths
  const mentorNavigationCards = [
    {
      title: 'Calendar',
      icon: calendarIcon,
      path: '/tutor/mentorship/calendar',
      description: 'Manage your mentorship schedule'
    },
    {
      title: 'Meetings',
      icon: meetingIcon,
      path: '/tutor/mentorship/meetings',
      description: 'Organize and track mentoring sessions'
    },
    {
      title: 'Mentees',
      icon: menteesIcon,
      path: '/tutor/mentorship/mentees',
      description: 'View and manage your mentees'
    },
    {
      title: 'My Space',
      icon: workspaceIcon,
      path: '/tutor/mentorship/study',
      description: 'Tutor Personal Space'
    }
  ];

  // # Handle mentor navigation
  const handleMentorNavigation = (path) => {
    const startTime = performance.now();
    
    console.log('Tutor Dashboard - Navigation:', {
      operation: 'mentor_navigation',
      path: path,
      timestamp: new Date().toISOString(),
      timeElapsed: `${(performance.now() - startTime).toFixed(2)}ms`
    });
    
    navigate(path);
  };

  // # Group dashboard items into sections
  const dashboardSections = {
    study: {
      title: 'USMLE Resources',
      items: [
        {
          title: 'Step 1 Library',
          icon: '/icons/dash/step_1.png',
          description: 'Access Step 1 study materials and resources',
          path: '/library/step1',
          locked: false
        },
        {
          title: 'Step 2 Library',
          icon: '/icons/dash/step_2.png',
          description: 'Access Step 2 study materials and resources',
          path: '/library/step2',
          locked: false
        }
      ]
    },
    personal: {
      title: 'Personal Tools',
      items: [
        {
          title: 'My Bookmarks',
          icon: '/icons/dash/bookmark.png',
          description: `${userDashboard?.stats?.bookmarksCount || 0} saved items`,
          path: '/bookmarks'
        },
        {
          title: 'My Notes',
          icon: '/icons/dash/notes.jpg',
          description: `${userDashboard?.stats?.notesCount || 0} study notes`,
          path: '/notes'
        },
        {
          title: 'My Flashcards',
          icon: '/icons/dash/flashcards.png',
          description: 'Create and study with flashcards',
          path: '/flashcards',
          locked: false
        },
        {
          title: 'Labs',
          icon: '/icons/dash/labs.png',
          description: 'Reference ranges for Blood, Serum, CSF, and Urine',
          path: '/labs'
        }
      ]
    },
    community: {
      title: 'TEMP-U Community',
      items: [
        {
          title: 'Taking Notes',
          icon: '/icons/dash/external_notes.png',
          description: 'Create and manage your study notes',
          path: '/community/notes'
        },
        {
          title: 'Guide',
          icon: '/icons/dash/guide.png',
          description: 'Comprehensive USMLE study guides',
          path: '/community/guide'
        },
        {
          title: 'Take a Break',
          icon: '/icons/dash/take_break.png',
          description: 'Relax and recharge 😴',
          path: '/community/break'
        },
        {
          title: 'Roadmaps',
          icon: '/icons/dash/roadmap.png',
          description: 'Study guides and learning paths',
          path: '/community/roadmaps'
        },
        {
          title: 'Content Updates',
          icon: '/icons/dash/updates.png',
          description: 'Latest platform updates and new content',
          path: '/community/updates'
        },
        {
          title: 'Clinical Rotations',
          icon: '/icons/dash/med_student.png',
          description: 'Clinical rotation resources and agencies',
          path: '/community/rotations'
        }
      ]
    },
    blog: {
      title: 'Blog',
      items: [
        {
          title: 'Articles',
          icon: '/icons/dash/article.png',
          description: 'View official articles',
          path: '/blog/articles'
        },
        {
          title: 'Q & A',
          icon: '/icons/dash/qa.png',
          description: 'Browse questions and answers',
          path: '/blog/qa'
        },
        {
          title: 'Experiences',
          icon: '/icons/dash/experiences.png',
          description: 'Share and learn from other students',
          path: '/community/experiences'
        },
        {
          title: 'Discussions',
          icon: '/icons/dash/discussion.png',
          description: 'Join community discussions',
          path: '/blog/discussions'
        }
      ]
    },
    extras: {
      title: 'TEMP-U Extras',
      items: [
        {
          title: 'Gallery',
          icon: '/icons/dash/gallery.png',
          description: 'Visual learning resources',
          path: '/extras/gallery'
        },
        {
          title: 'Clinical Library',
          icon: '/icons/dash/clinical.png',
          description: 'Clinical case studies and resources',
          path: '/extras/clinical'
        },
        {
          title: 'Research Library',
          icon: '/icons/dash/laboratory.png',
          description: 'Research papers and publications',
          path: '/extras/research'
        },
        {
          title: 'OET',
          icon: '/icons/dash/oet.png',
          description: 'OET preparation materials',
          path: '/extras/oet'
        }
      ]
    },
    support: {
      title: 'Support & Settings',
      items: [
        {
          title: 'Settings',
          icon: '/icons/dash/settings.png',
          description: 'Manage your account settings and preferences',
          path: '/settings'
        },
        {
          title: 'Support',
          icon: '/icons/dash/support.png',
          description: 'Get help and support for any issues',
          path: '/support'
        },
        {
          title: 'Feedback',
          icon: '/icons/dash/feedback.jpg',
          description: 'Share your thoughts and suggestions',
          path: '/feedback'
        },
        {
          title: 'Reviews',
          icon: '/icons/dash/reviews.png',
          description: 'Read success stories from our students',
          path: '/reviews'
        }
      ]
    }
  };

  // # Handle sign out
  const handleSignOut = async () => {
    try {
      const { signOut } = useAuthStore.getState(); // Get signOut from store
      await signOut(); // Use the store's signOut method
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Error signing out. Please try again.');
    }
  };

  return (
    <div className={`ttr_dsh_container ${isDarkMode ? 'ttr_dsh_dark-mode' : ''}`}>
      {/* # Pass theme props to header */}
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />

      {/* # Cover Image */}
      <div className="ttr_dsh_cover-section">
        <img 
          src="/icons/cover_ocean.jpg" 
          alt="Cover" 
          className="ttr_dsh_cover-image"
        />
        
        {/* # Title Logo */}
        <div className="ttr_dsh_title-logo">
          <FaUserGraduate />
          <h1>Tutor Area</h1>
        </div>
      </div>

      {/* # Profile Section */}
      <div className="ttr_dsh_profile-section">
        <div 
          className="ttr_dsh_profile-image-container"
          onClick={() => navigate('/profile')}
        >
          <img 
            src={getProfilePhotoUrl(userProfile)}
            alt="Profile" 
            className="ttr_dsh_profile-image"
          />
        </div>
        <div className="ttr_dsh_profile-info">
          <h2 className="ttr_dsh_profile-name">
            {displayName.name}
          </h2>
          <div className="ttr_dsh_user_username">
            {displayName.username}
          </div>
        </div>
      </div>

      {/* # Mentor Navigation Cards */}
      <div className="ttr_dsh_mentor-section">
        <h2 className="ttr_dsh_section-title">Mentorship Portal</h2>
        <div className="ttr_dsh_mentor-grid">
          {mentorNavigationCards.map((card) => (
            <div 
              key={card.title} 
              className="ttr_dsh_mentor-card"
              onClick={() => handleMentorNavigation(card.path)}
            >
              <img src={card.icon} alt={card.title} className="ttr_dsh_mentor-icon" />
              <h3>{card.title}</h3>
              <p>{card.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* # Enhanced Dashboard Sections with Carousel */}
      <UserDashboardUX sections={dashboardSections} isDarkMode={isDarkMode} />

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
};

export default TutorDashboard;
